import React from 'react';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { TableSettingsContext, TableSettingsContextProps } from '../context/TableSettingsContext';
import { TableSettings } from '../types/table_settings/TableSettings';
import { EntityType } from '../types/comment';
import { AssetTableSettings } from '../types/table_settings/asset/AssetTableSettings';
import { ProductMasterDataTableSettings } from '../types/table_settings/pmd/ProductMasterDataTableSettings';
import { OrderTableSettings } from '../types/table_settings/order/OrderTableSettings';
import { ProductTableSettings } from '../types/table_settings/product/ProductTableSettings';
import { OrderProductTransactionTableSettings } from '../types/table_settings/productTransaction/OrderProductTransactionTableSettings';
import { ContainerTableSettings } from '../types/table_settings/container/ContainerTableSettings';
import { LotTableSettings } from '../types/table_settings/lot/LotTableSettings';

const tableSettingsState = {
  items: new Map<string, TableSettings<any>>(),
  loading: true,
};

export const tableSettingsSlice = createSlice({
  name: 'tableSettings',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      tableSettingsState.items = new Map();
      tableSettingsState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<TableSettings<any>[]>) => {
      tableSettingsState.items = new Map();
      action.payload.forEach(p => tableSettingsState.items.set(p.id, new TableSettings(p)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<TableSettings<any>[]>) => {
      action.payload.forEach(p => tableSettingsState.items.set(p.id, new TableSettings(p)));
      tableSettingsState.items = new Map(tableSettingsState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<TableSettings<any>[]>) => {
      action.payload.forEach(p => tableSettingsState.items.delete(p.id));
      tableSettingsState.items = new Map(tableSettingsState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      tableSettingsState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface TableSettingsProviderProps {
  children: React.ReactNode;
}

export default function TableSettingsProvider({ children }: TableSettingsProviderProps) {
  const dispatch = useAppDispatch();
  useAppSelector(state => state.tableSettings.key);

  const getDefaultSettings = (type: EntityType, companyId: string, parentType?: EntityType) => {
    switch (type) {
      case EntityType.asset:
        return AssetTableSettings.default(companyId);
      case EntityType.order:
        return OrderTableSettings.default(companyId);
      case EntityType.pmd:
        return ProductMasterDataTableSettings.default(companyId);
      case EntityType.product:
        return ProductTableSettings.default(companyId);
      case EntityType.container:
        return ContainerTableSettings.default(companyId);
      case EntityType.lot:
        return LotTableSettings.default(companyId);
      case EntityType.productTransaction:
        if (parentType === EntityType.order) return OrderProductTransactionTableSettings.default(companyId);

        throw new Error(`No default table settings defined for type ${type} with parent type ${parentType}`);
      default:
        throw new Error(`No default table settings defined for type ${type}`);
    }
  };

  const context: TableSettingsContextProps = {
    tableSettings: tableSettingsState.items,
    tableSettingsLoading: tableSettingsState.loading,
    setTableSettings: (ts: Map<string, TableSettings<any>>) => {
      dispatch(tableSettingsSlice.actions.set([...ts.values()]));
    },
    getTableSettings: (type, companyId, parentType) => {
      const tableSettings = Array.from(tableSettingsState.items.values()).find(
        ts =>
          ts.type === type &&
          ts.companyId === companyId &&
          (parentType == null ? ts.parentType == null : ts.parentType === parentType),
      );

      return tableSettings ?? getDefaultSettings(type, companyId, parentType);
    },
  };

  return <TableSettingsContext.Provider value={context}>{children}</TableSettingsContext.Provider>;
}
